import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import DataParser from "../../utlits/Dataparser";
import NotificationService from "../../Services/NotificationService";
import SocketService from "../../Services/SocketService";
import moment from "moment";
import UserProfile from "./UserProfile.";
import LoginService from "../../Services/LoginService";
import { IoNotifications } from "react-icons/io5";

const TABS = ["All", "Unread", "Mentions"];

const isMentionNotif = (n) => {
  const t = (n.title || "").toLowerCase().trim();
  return t.startsWith("mentioned you");
};

const isChatMentionNotif = (cn) => cn.isMention === true;

const Notofication = () => {
  const [allList, setAllList] = useState([]);
  const [chatNotifList, setChatNotifList] = useState([]);
  const [activeTab, setActiveTab] = useState("All");
  const navigate = useNavigate();

  // Derived lists — mentions and non-mentions are mutually exclusive across tabs
  const unreadList = allList.filter((n) => n.readed === 0 && !isMentionNotif(n));
  const unreadChatList = chatNotifList.filter((cn) => !isChatMentionNotif(cn));
  const mentionUnreadList = allList.filter((n) => n.readed === 0 && isMentionNotif(n));
  const mentionChatList = chatNotifList.filter(isChatMentionNotif);
  const mentionCount = mentionUnreadList.length + mentionChatList.length;

  const formatNotificationTime = (date) => {
    const now = moment();
    const created = moment(date);
    const diffInHours = now.diff(created, "hours");
    if (diffInHours < 24) return created.fromNow();
    const diffInDays = now.diff(created, "days");
    return diffInDays === 1 ? "1 day ago" : `${diffInDays} days ago`;
  };

  const fetchNotifications = () => {
    const companyid = DataParser.CompanyId();
    // Try fetching all (read + unread); fall back to unread-only if it fails or returns nothing
    NotificationService.getCompanyNotifications(companyid, true)
      .then((res) => {
        const data = res?.data?.notifications;
        if (data === "Un-Athunticated request") {
          LoginService.logout();
          navigate("/");
          return;
        }
        if (Array.isArray(data) && data.length > 0) {
          setAllList(data);
        } else {
          // Backend doesn't support include_read yet — fall back to unread
          NotificationService.getCompanyNotifications(companyid, false)
            .then((r) => setAllList(r?.data?.notifications || []))
            .catch(() => {});
        }
      })
      .catch(() => {
        // Network/server error — fall back to unread
        NotificationService.getCompanyNotifications(companyid, false)
          .then((r) => setAllList(r?.data?.notifications || []))
          .catch(() => {});
      });
  };

  useEffect(() => {
    fetchNotifications();

    const playNotificationSound = () => {
      const sound = new Audio("https://assets.mixkit.co/active_storage/sfx/2358/2358-preview.mp3");
      try { sound.volume = 0.4; sound.play().catch(() => {}); } catch (err) {}
    };

    // Refresh data every time the dropdown is opened
    const toggleBtn = document.getElementById("page-header-notifications-dropdown");
    const handleDropdownShow = () => fetchNotifications();
    if (toggleBtn) toggleBtn.addEventListener("show.bs.dropdown", handleDropdownShow);

    SocketService.connect();
    const handleNotification = (data) => {
      const currentUserId = DataParser.UserId();
      if (String(data.assigned_to) === String(currentUserId)) {
        playNotificationSound();
        fetchNotifications();
      }
    };
    SocketService.on("notification", handleNotification);

    const handleChatNotif = (e) => {
      setChatNotifList((prev) => {
        const filtered = prev.filter((n) => n.roomId !== e.detail.roomId);
        return [e.detail, ...filtered].slice(0, 10);
      });
      playNotificationSound();
    };
    window.addEventListener("chatNotification", handleChatNotif);

    return () => {
      if (toggleBtn) toggleBtn.removeEventListener("show.bs.dropdown", handleDropdownShow);
      SocketService.off("notification", handleNotification);
      window.removeEventListener("chatNotification", handleChatNotif);
    };
  }, []);

  const readNotification = (e, item) => {
    e.preventDefault();
    const type = item.typ;
    const notificationId = item.id;
    const taskId = item.task_id;
    const leadId = item.lead_id || item.mod_id || (type === "lead" || type === "leads" ? item.task_id : null);
    const companyIdetifier = DataParser.CompanyIdentifier();
    const companyId = DataParser.CompanyId();

    // Optimistically mark as read — no refetch to avoid overwriting before DB commits
    setAllList((prev) => prev.map((n) => n.id === item.id ? { ...n, readed: 1 } : n));
    NotificationService.readNotification(notificationId, companyId);

    if (type === "task") {
      navigate(`/${companyIdetifier}/tasks`, { state: { TaskId: taskId, isNotification: true } });
    } else if (type === "project") {
      navigate(`/${companyIdetifier}/projects`, { state: { TaskId: taskId, isNotification: true } });
    } else if (type === "leave") {
      const isHead = DataParser.isHead() === "yes";
      navigate(`/${companyIdetifier}/${isHead ? "leaves_team" : "employee_leaves"}`, { state: { isNotification: true } });
    } else if (type === "attendance") {
      let targetMonth = null, targetYear = null;
      const dateMatch = item.title ? item.title.match(/for (\d{1,2} [A-Za-z]{3} \d{4})/) : null;
      if (dateMatch) {
        const parsedDate = moment(dateMatch[1], "DD MMM YYYY");
        if (parsedDate.isValid()) { targetMonth = parsedDate.format("MM"); targetYear = parsedDate.format("YYYY"); }
      }
      navigate(`/${companyIdetifier}/employee-attendance`, { state: { data: item.assigned_to, month: targetMonth, year: targetYear, isNotification: true } });
    } else if (type === "evaluation") {
      navigate(`/${companyIdetifier}/evaluation`, { state: { isNotification: true } });
    } else if (type === "lead" || type === "leads") {
      navigate(`/${companyIdetifier}/crm-leads`, {
        state: {
          LeadId: leadId, isNotification: true,
          isMention: String(item?.title || "").toLowerCase().includes("mention") || String(item?.title || "").toLowerCase().includes("comment"),
        },
      });
    } else if (type === "chat" || type === "channel") {
      // Channel invite: identifier holds the token — ChatContext asks Accept/Decline first.
      // Normal chat notifications open the room directly.
      const roomId = item.description;
      const isInvite = String(item.title || "").toLowerCase().includes("invited you");
      if (isInvite && item.identifier) {
        const subject = String(item.email_subject || "");
        const marker = " invited you to ";
        const idx = subject.indexOf(marker);
        window.dispatchEvent(new CustomEvent("chatInvitePrompt", {
          detail: {
            token: item.identifier,
            roomId,
            inviterName: [item.assigned?.first_name, item.assigned?.last_name].filter(Boolean).join(" "),
            roomName: idx >= 0 ? subject.slice(idx + marker.length) : "",
          },
        }));
      } else if (roomId) {
        window.dispatchEvent(new CustomEvent("openChatRoom", { detail: { roomId } }));
      }
    } else if (type === "meeting") {
      if (item.meeting_id) navigate(`/${DataParser.CompanyIdentifierRole()}/meetings/${item.meeting_id}`);
    } else if (type === "note") {
      navigate(`/${DataParser.CompanyIdentifierRole()}/notes`, { state: { view: "shared", noteId: item.note_id } });
    } else {
      navigate(`/${companyIdetifier}/crm-leads`, { state: { LeadId: leadId, isNotification: true, isMention: false } });
    }
  };

  const getDisplayLists = () => {
    if (activeTab === "Unread") return { systemList: unreadList, chatList: unreadChatList };
    if (activeTab === "Mentions") return { systemList: mentionUnreadList, chatList: mentionChatList };
    return { systemList: allList, chatList: chatNotifList };
  };

  const { systemList, chatList } = getDisplayLists();
  const hasUnread = unreadList.length > 0 || unreadChatList.length > 0 || mentionUnreadList.length > 0 || mentionChatList.length > 0;
  const isEmpty = systemList.length === 0 && chatList.length === 0;

  return (
    <div className="dropdown d-inline-block" style={{ position: "relative" }}>
      <button
        type="button"
        className="btn header-item noti-icon waves-effect dropdown-toggle"
        id="page-header-notifications-dropdown"
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
        aria-haspopup="true"
        aria-expanded="true"
      >
        <span style={{ position: "relative", display: "inline-block", width: "22px", height: "22px", lineHeight: "22px", verticalAlign: "middle" }}>
          <IoNotifications style={{ fontSize: "22px", color: "#1D1D1D", display: "block" }} />
          {hasUnread && (
            <span style={{
              position: "absolute", top: "3px", right: "3px",
              transform: "translate(40%, -40%)", width: "9px", height: "9px",
              borderRadius: "50%", backgroundColor: "#0DDFAB",
              display: "block", border: "2px solid #fff", zIndex: 1,
            }} />
          )}
        </span>
      </button>

      <div
        className="dropdown-menu dropdown-menu-lg dropdown-menu-end p-0 notification"
        aria-labelledby="page-header-notifications-dropdown"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-3 pb-0">
          <div className="row align-items-center">
            <div className="col">
              <h5 className="m-0 font-size-16">Inbox</h5>
            </div>
            <div className="col-auto" style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              {allList.some((n) => n.readed === 0) && (
                <span
                  className="small"
                  style={{ color: "#556ee6", fontWeight: 600, textDecoration: "none", cursor: "pointer", fontSize: "12px" }}
                  onClick={() => {
                    setAllList((prev) => prev.map((n) => ({ ...n, readed: 1 })));
                    NotificationService.markAllRead(DataParser.CompanyId());
                  }}
                >
                  Mark all read
                </span>
              )}
              <Link
                to={`/${DataParser.CompanyIdentifier()}/inbox`}
                className="small"
                style={{ color: "#0DDFAB", fontWeight: 600, textDecoration: "none" }}
                onClick={() => {
                  const el = document.getElementById("page-header-notifications-dropdown");
                  if (el && window.bootstrap) {
                    const dd = window.bootstrap.Dropdown.getInstance(el);
                    if (dd) dd.hide();
                  }
                }}
              >
                View all
              </Link>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ display: "flex", marginTop: "10px", borderBottom: "1px solid #f0f0f0" }}>
            {TABS.map((tab) => (
              <button
                key={tab}
                onClick={(e) => { e.stopPropagation(); setActiveTab(tab); }}
                style={{
                  flex: 1, background: "none", border: "none",
                  borderBottom: activeTab === tab ? "2px solid #0DDFAB" : "2px solid transparent",
                  padding: "6px 4px",
                  fontSize: "11px",
                  fontWeight: activeTab === tab ? "700" : "500",
                  color: activeTab === tab ? "#1D1D1D" : "#888",
                  cursor: "pointer",
                  transition: "all 0.15s",
                }}
              >
                {tab}
                {tab === "Unread" && (unreadList.length + unreadChatList.length) > 0 && (
                  <span style={{
                    marginLeft: "4px", background: "#0DDFAB", color: "#000",
                    borderRadius: "8px", fontSize: "9px", padding: "1px 5px", fontWeight: "700",
                  }}>
                    {unreadList.length + unreadChatList.length}
                  </span>
                )}
                {tab === "Mentions" && mentionCount > 0 && (
                  <span style={{
                    marginLeft: "4px", background: "#0DDFAB", color: "#000",
                    borderRadius: "8px", fontSize: "9px", padding: "1px 5px", fontWeight: "700",
                  }}>
                    {mentionCount}
                  </span>
                )}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div data-simplebar="init" style={{ maxHeight: 230 }}>
          <div className="simplebar-wrapper" style={{ margin: 0 }}>
            <div className="simplebar-height-auto-observer-wrapper">
              <div className="simplebar-height-auto-observer" />
            </div>
            <div className="simplebar-mask">
              <div className="simplebar-offset" style={{ right: "-17px", bottom: 0 }}>
                <div className="simplebar-content-wrapper" style={{ height: "auto", overflow: "hidden scroll" }}>
                  {!isEmpty ? (
                    <div className="simplebar-content" style={{ padding: 0 }}>
                      {/* Chat notifications */}
                      {chatList.map((cn) => (
                        <Link
                          key={cn.id}
                          to="#"
                          className="text-reset notification-item"
                          style={{ textDecoration: "none" }}
                          onClick={(e) => {
                            e.preventDefault();
                            setChatNotifList((prev) => prev.filter((n) => n.id !== cn.id));
                            window.dispatchEvent(new CustomEvent("openChatRoom", { detail: { roomId: cn.roomId } }));
                          }}
                        >
                          <div className="d-flex align-items-start" style={{ gap: "6px", padding: "4px 4px" }}>
                            {/* Green dot — chat notifs are always unread */}
                            <div style={{ width: "6px", flexShrink: 0, display: "flex", justifyContent: "center", paddingTop: "4px" }}>
                              <span style={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: "#0DDFAB", display: "block" }} />
                            </div>
                            <div style={{ flexShrink: 0 }}>
                              <UserProfile username={cn.senderName} userImage={cn.senderImage} size="small" style={{ width: "20px", height: "20px" }} />
                            </div>
                            <div style={{ flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                              <div style={{ fontSize: "10px", lineHeight: "1.3" }}>
                                <span style={{ fontWeight: 600 }}>{cn.senderName}</span>
                                {" "}
                                <span style={{ color: "#555" }}>{cn.title}</span>
                              </div>
                              <div style={{ fontSize: "9px", color: "#888", marginTop: "2px", display: "flex", alignItems: "center", gap: "3px" }}>
                                <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                <span>{formatNotificationTime(cn.createdAt)}</span>
                              </div>
                            </div>
                          </div>
                        </Link>
                      ))}

                      {/* System notifications */}
                      {systemList.map((el) => {
                        const isRead = el.readed === 1;
                        return (
                          <Link
                            key={el.id}
                            to="#"
                            className="text-reset notification-item"
                            onClick={(e) => readNotification(e, el)}
                            style={{ textDecoration: "none", opacity: isRead ? 0.7 : 1 }}
                          >
                            <div className="d-flex align-items-start" style={{ gap: "6px", padding: "4px 4px" }}>
                              {/* Unread dot */}
                              <div style={{ width: "6px", flexShrink: 0, display: "flex", justifyContent: "center", paddingTop: "4px" }}>
                                {!isRead && (
                                  <span style={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: "#0DDFAB", display: "block" }} />
                                )}
                              </div>
                              <div style={{ flexShrink: 0 }}>
                                {el?.assigned?.first_name ? (
                                  <UserProfile
                                    username={`${el.assigned.first_name} ${el.assigned.last_name || ""}`.trim()}
                                    userImage={el.assigned.image}
                                    size="small"
                                    style={{ width: "20px", height: "20px" }}
                                  />
                                ) : (
                                  <div style={{ width: "20px", height: "20px", borderRadius: "50%", background: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "11px", flexShrink: 0 }}>
                                    🔔
                                  </div>
                                )}
                              </div>
                              <div style={{ flexGrow: 1, display: "flex", flexDirection: "column", minWidth: 0 }}>
                                <div style={{ fontSize: "10px", lineHeight: "1.3" }}>
                                  <span style={{ fontWeight: 600 }}>
                                    {el?.assigned?.first_name
                                      ? `${el.assigned.first_name} ${el.assigned.last_name || ""}`.trim()
                                      : "System"}
                                  </span>
                                  {" "}
                                  <span style={{ color: "#555" }}>{el?.title}</span>
                                </div>
                                <div style={{ fontSize: "9px", color: "#888", marginTop: "2px", display: "flex", alignItems: "center", gap: "3px" }}>
                                  <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="#888" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                                  <span>{formatNotificationTime(el?.createdAt)}</span>
                                </div>
                              </div>
                            </div>
                          </Link>
                        );
                      })}
                    </div>
                  ) : (
                    <div style={{ textAlign: "center", padding: "20px 0" }}>
                      <p className="font-size-12 text-muted mb-1">
                        {activeTab === "Unread" ? "No unread notifications" : activeTab === "Mentions" ? "No mentions yet" : "No notifications"}
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </div>
            <div className="simplebar-placeholder" style={{ width: "auto", height: 359 }} />
          </div>
          <div className="simplebar-track simplebar-horizontal" style={{ visibility: "hidden" }}>
            <div className="simplebar-scrollbar" style={{ transform: "translate3d(0px, 0px, 0px)", display: "none" }} />
          </div>
          <div className="simplebar-track simplebar-vertical" style={{ visibility: "visible" }}>
            <div className="simplebar-scrollbar" style={{ transform: "translate3d(0px, 0px, 0px)", display: "block", height: 147 }} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default Notofication;
